// ---------------------------------------------------------------------------
// V13. WHAT THE CREW SCREEN DERIVES FROM A STATE — AND IT IS A FILE, NOT A
// COMPONENT.
//
// tsconfig.test.json's own note says why: "the rules that matter most are
// decided in files a node test can execute, rather than inside a component."
// These two are exactly that. The tone is what a scheduler reads at a glance
// across forty rows; the accessible name is the WHOLE of the information for
// somebody who cannot see the tone, which 11-console-design-system.md §6
// established with measurement — the four semantic colours sit within 1.02:1 to
// 1.72:1 of each other, so in greyscale they are indistinguishable.
//
// NO STATE ID IS NAMED HERE EITHER. Both functions take the two booleans that
// travel with every row and ask what the state DOES, never what it is called.
// ---------------------------------------------------------------------------

import type { Tone } from "../design/tone";
import { english, type Translate } from "../i18n/translate";

/**
 * Just enough of a crew row to answer both questions.
 *
 * STRUCTURAL RATHER THAN `CrewAssignment`, so this module depends on nothing —
 * api/console.ts is not in the node test build and importing it would drag the
 * whole client in to test two pure functions. TypeScript checks the real row
 * satisfies it at every call site, which is the guarantee that mattered.
 */
export type CrewRow = {
  worker_name: string;
  role_name: string;
  status_label: string;
  status_fills_headcount: boolean;
  status_is_terminal: boolean;
  is_reserve: boolean;
};

/**
 * What tone a row's state deserves, asked of the state's BEHAVIOUR.
 *
 * `fills_headcount` is the same column the database counts when it decides
 * whether a requirement is full, and the same one V17's score will read. Using
 * it here is what stops the board and the score disagreeing — 12-… §V14.2's
 * whole argument, applied a slice early because this list is where a scheduler
 * first learns to trust the numbers.
 */
export function toneFor(row: { status_fills_headcount: boolean; status_is_terminal: boolean }): Tone {
  // The place is taken. Somebody is committed to this shift.
  if (row.status_fills_headcount) return "live";
  // The place is free again and nothing further will happen on its own. That is
  // a thing needing a human, which is what attention means.
  if (row.status_is_terminal) return "attention";
  return "neutral";
}

/** The sentence a screen reader gets. State AND cause, never colour. */
export function accessibleName(row: CrewRow, t: Translate = english): string {
  const place = row.is_reserve
    ? t("standby, not counted against the headcount")
    : row.status_fills_headcount
      ? t("holding a place")
      : t("not holding a place");
  return `${row.worker_name}, ${row.role_name}, ${row.status_label}, ${place}`;
}
